import { useDispatch } from 'react-redux';

import { SYSTEMS } from '../../constants/systems';
import { isAnotherSystemRunning } from '../../helpers/helpers';
import { logger } from '../../helpers/logger';
import { schedulePayload } from '../../helpers/payloads/schedulePayload';
import { useDeviceCommand } from '../../hooks/useDeviceCommand';
import { useTemperature } from '../../hooks/useTemperature';
import {
  createScheduleService,
  deleteScheduleService,
  updateScheduleService,
} from '../../services/schedule.service';
import { SWITCH_SLICES } from '../store/slices/switchSlices';

const HEATING_PROGRAMS = ['instantHeat', 'snowSensor', 'windFactor', 'heatingSchedule'];

/**
 * What a control box's widget buttons do to one device: send the command,
 * update the device in Redux, ask first while the device's other system runs
 * (TGS/TES), and save or delete heating schedules.
 *
 * `showMessage(id, program)` opens the box's message for a refused input
 * (`id`: 'tempA', 'tempB', 'tempMissing' or 'fanOnly').
 * `clearInstantHeatInput()` empties the instant heat field.
 */
export const useProgramCommands = ({
  system,
  location,
  machine,
  device,
  showMessage,
  clearInstantHeatInput,
}) => {
  const dispatch = useDispatch();
  const { fromInput } = useTemperature();
  const { deviceType, conflict } = SYSTEMS[system];
  const actions = SWITCH_SLICES[system];
  const { deviceMac, heatingScheduleList, currentRun, devicesConflicts } = device;
  const sendCommand = useDeviceCommand(system, deviceMac, device.device_name);
  const at = { location, machine };

  // Opens the "other system is running" question instead of the command.
  // Returns false, asking nothing, when the other system isn't running.
  const askFirst = (commandTarget, extraData) => {
    if (!conflict || !isAnotherSystemRunning(currentRun, conflict.otherRun)) {
      return false;
    }
    dispatch(actions.showConflict(at));
    dispatch(
      actions.setConflict({
        ...at,
        currentSwitch: conflict.currentSwitch,
        desiredSwitch: conflict.desiredSwitch,
        systemTarget: conflict.systemTarget,
        commandTarget,
        ...(extraData !== undefined && { extraData }),
      }),
    );
    return true;
  };

  const startInstantHeat = (temp) => {
    sendCommand({ on_switch: 1, instant_temp: temp });
    dispatch(actions.instantHeatOn({ ...at, temp }));
  };

  const startConstantTemp = (temp) => {
    sendCommand({ on_constant: 1, constant_temp: temp });
    dispatch(actions.constantTempOn({ ...at, temp }));
  };

  const startSnowSensor = () => {
    sendCommand({ snow_enabled: 1 });
    dispatch(actions.snowSensorOn(at));
  };

  const startWindFactor = () => {
    sendCommand({ wind: 1 });
    dispatch(actions.windFactorOn(at));
  };

  // Creates the schedule in row `index`, or updates the row if it is saved.
  const saveSchedule = ({ start, end, temp, index }) => {
    const id = heatingScheduleList[index]?.id;
    const body = schedulePayload({ deviceMac, deviceType, start, end, temp });
    (id ? updateScheduleService(id, body) : createScheduleService(body))
      .then((res) =>
        dispatch(actions.addSchedule({ ...at, start, end, index, inputTemp: temp, id: res.id })),
      )
      .catch((e) => logger.error(`${id ? 'update' : 'create'} heating schedule fail:`, e.message));
  };

  // Deletes the saved schedule in row `index`. The last row is the empty one
  // the operator types into, so clearing it does nothing.
  const deleteSchedule = (index) => {
    if (index === heatingScheduleList.length - 1) return;
    const data = heatingScheduleList.filter((schedule, idx) => idx !== index && schedule);
    deleteScheduleService(heatingScheduleList[index]?.id)
      .then(() => dispatch(actions.clearSchedule({ ...at, data })))
      .catch((err) => logger.error('delete heating schedule fail:', err));
  };

  const onInstantHeat = (state, temp) => {
    if (state === 'on') {
      if (!askFirst('on_switch', temp)) startInstantHeat(temp);
    } else if (state === 'off') {
      sendCommand({ on_switch: 0 });
      dispatch(actions.instantHeatOff(at));
    } else {
      clearInstantHeatInput();
      showMessage(state, 'instant heat');
    }
  };

  const onConstantTemp = (state, temp) => {
    if (state === 'on') {
      if (!askFirst('on_constant', temp)) startConstantTemp(temp);
    } else if (state === 'off') {
      sendCommand({ on_constant: 0 });
      dispatch(actions.constantTempOff(at));
    } else {
      showMessage(state, 'optional constant temp.');
    }
  };

  const onSnowSensor = (state) => {
    if (state === 'off') {
      sendCommand({ snow_enabled: 0 });
      dispatch(actions.snowSensorOff(at));
    } else if (!askFirst('snow_enabled')) {
      startSnowSensor();
    }
  };

  const onWindFactor = (state) => {
    if (state === 'off') {
      sendCommand({ wind: 0 });
      dispatch(actions.windFactorOff(at));
    } else if (!askFirst('wind')) {
      startWindFactor();
    }
  };

  // TGS: the fan has to keep running while any heating program does.
  const onFanOnly = (state) => {
    if (state === 'on') {
      sendCommand({ fan: 1 });
      dispatch(actions.fanOnly({ ...at, state: true }));
    } else if (HEATING_PROGRAMS.some((program) => device[program].isActivated)) {
      showMessage('fanOnly', 'fan only');
    } else {
      sendCommand({ fan: 0 });
      dispatch(actions.fanOnly({ ...at, state: false }));
    }
  };

  const onHeatingSchedule = (state, temp, data, index) => {
    if (state === 'clear') {
      deleteSchedule(index);
      return;
    }
    const schedule = { start: data.start, end: data.end, temp, index };
    if (!askFirst('schedule', schedule)) saveSchedule(schedule);
  };

  // Widgets report (program, input, temperature as typed, schedule, row).
  const handleOnClick = (id, state, typedTemp, data, index) => {
    // From here on the temperature is °C.
    const temp = state === 'on' ? fromInput(typedTemp) : typedTemp;
    switch (id) {
      case 'instantHeat':
        onInstantHeat(state, temp);
        break;
      case 'constantTemp':
        onConstantTemp(state, temp);
        break;
      case 'snowSensor':
        onSnowSensor(state);
        break;
      case 'windFactor':
        onWindFactor(state);
        break;
      case 'fanOnly':
        onFanOnly(state);
        break;
      case 'heatingSchedule':
        onHeatingSchedule(state, temp, data, index);
        break;
      default:
        break;
    }
  };

  // Runs the command the operator confirmed over the other system.
  const confirmConflict = () => {
    const { commandTarget, extraData } = devicesConflicts;
    if (commandTarget === 'on_switch') startInstantHeat(extraData);
    if (commandTarget === 'on_constant') startConstantTemp(extraData);
    if (commandTarget === 'snow_enabled') startSnowSensor();
    if (commandTarget === 'wind') startWindFactor();
    if (commandTarget === 'schedule') saveSchedule(extraData);
    dispatch(actions.hideConflict(at));
  };

  const cancelConflict = () => dispatch(actions.hideConflict(at));

  return { handleOnClick, confirmConflict, cancelConflict };
};
